import { createFlagsmithInstance, IFlagsmith, IState } from 'flagsmith';

/**
 * Service for managing Flagsmith feature flags
 * Provides centralized methods for flag operations
 */
class FeatureFlagService {
    private static instance: IFlagsmith;

    /**
     * Get or create the Flagsmith instance (lazy initialization)
     */
    get flagsmith(): IFlagsmith {
        if (!FeatureFlagService.instance) {
            FeatureFlagService.instance = createFlagsmithInstance();
        }
        return FeatureFlagService.instance;
    }

    /**
     * Check if a feature flag is enabled
     * @param flagKey - The feature flag key
     * @returns true if the flag is enabled, false otherwise
     */
    isFeatureEnabled(flagKey: string): boolean {
        try {
            return this.flagsmith.hasFeature(flagKey);
        } catch (error) {
            console.warn(`[FeatureFlagService] Error checking flag "${flagKey}":`, error);
            return false;
        }
    }

    /**
     * Get the value of a feature flag
     * @param defaultValue - Default value to return if flag doesn't exist
     * @returns The flag value or default value
     */
    getFlagValue<T = string>(flagKey: string, defaultValue?: T): T | undefined {
        try {
            const value = this.flagsmith.getValue(flagKey, {
                json: true
            });
            return (value as T) ?? defaultValue;
        } catch (error) {
            console.warn(`[FeatureFlagService] Error getting flag value "${flagKey}":`, error);
            return defaultValue;
        }
    }

    /**
     * Log flags status for debugging (development only)
     * @param flagKeys - Specific keys to log (optional)
     */
    logFlagsStatus(flagKeys?: string[]): void {
        if (process.env.NODE_ENV !== 'development') return;

        const flags = this.flagsmith.getAllFlags();
        const keysToLog = flagKeys || Object.keys(flags || {});

        console.info('[FeatureFlagService] Feature flags status:');
        keysToLog.forEach((key) => {
            const enabled = this.flagsmith.hasFeature(key);
            const value = this.flagsmith.getValue(key, { json: true });
            console.info(`${enabled ? '🟢' : '❌'} ${String(key)}:`, { enabled, value });
        });
    }

    /**
     * Initialize Flagsmith for Server-Side Rendering
     * @returns Promise containing the Flagsmith state for SSR
     */
    async initialize(): Promise<{ flagsmithState: IState }> {
        if (!process.env.NEXT_PUBLIC_FLAGSMITH_KEY) {
            console.warn('[FeatureFlagService] No Flagsmith key found, returning empty state');
            return { flagsmithState: { api: '', identity: null, flags: {} } };
        }

        try {
            await this.flagsmith.init({
                environmentID: process.env.NEXT_PUBLIC_FLAGSMITH_KEY,
                api: process.env.NEXT_PUBLIC_FLAGSMITH_API_URL
            });

            return { flagsmithState: this.flagsmith.getState() };
        } catch (error) {
            console.warn('[FeatureFlagService] Error initializing SSR:', error);
            return { flagsmithState: { api: '', identity: null, flags: {} } };
        }
    }
}

const flagsmithService = new FeatureFlagService();
export { flagsmithService as FeatureFlagService };
